/**
 * Amounts, for READING. There is no arithmetic in this file and none anywhere
 * else in apps/web.
 *
 * Splitting evenly with the remainder on the first share is implemented once,
 * in services/api/valley/domain.py, ported verbatim from apps/display/server.py.
 * Two implementations drift, and when they drift a family's money is wrong --
 * so the client formats and never computes.
 *
 * Amounts leave this app as the STRING a parent typed. The server parses it
 * with the same `money()` the reference implementation used, so "20", "20.5"
 * and "20.50" mean what they look like and rounding happens in one place.
 *
 * WHAT A FAMILY COUNTS IN (2026-09-26). Every amount is stored in hundredths
 * whatever the unit -- cents, when it is money -- and the unit decides only how
 * it is SHOWN and whether parts of one may be typed (services/api/valley/
 * units.py). The unit is the family's, one at a time, so it is held here as
 * module state and set by whatever loads the family (`loadState` for a parent,
 * `loadOwn` for a child). Formatting is then a plain call from anywhere, as it
 * was when it was always dollars.
 */

export type Unit =
  | { type: 'currency'; currency: string }
  | { type: 'marbles' }
  | { type: 'custom'; one: string; many: string; icon: string; whole: boolean };

export const DEFAULT_UNIT: Unit = { type: 'currency', currency: 'USD' };

/**
 * The currencies Valley offers: two decimal places each, so "hundredths" is
 * exactly their minor unit. Must match CURRENCIES in units.py.
 */
export const CURRENCIES: ReadonlyArray<[code: string, name: string]> = [
  ['USD', 'US dollar'], ['CAD', 'Canadian dollar'], ['AUD', 'Australian dollar'],
  ['NZD', 'New Zealand dollar'], ['GBP', 'Pound sterling'], ['EUR', 'Euro'],
  ['CHF', 'Swiss franc'], ['SEK', 'Swedish krona'], ['NOK', 'Norwegian krone'],
  ['DKK', 'Danish krone'], ['PLN', 'Polish złoty'], ['INR', 'Indian rupee'],
  ['ZAR', 'South African rand'], ['SGD', 'Singapore dollar'], ['HKD', 'Hong Kong dollar'],
  ['MXN', 'Mexican peso'], ['BRL', 'Brazilian real'], ['PHP', 'Philippine peso'],
  ['AED', 'UAE dirham'], ['NGN', 'Nigerian naira'], ['KES', 'Kenyan shilling'],
];

let unit: Unit = DEFAULT_UNIT;
const currencyFormats = new Map<string, { full: Intl.NumberFormat; short: Intl.NumberFormat }>();
const plain = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

/** Set by whatever loads the family. Every formatter below follows it. */
export function setUnit(next: Unit): void {
  unit = next;
}

export function currentUnit(): Unit {
  return unit;
}

/** True when only whole amounts can be typed: marbles, and most custom units. */
export function isWhole(of: Unit = unit): boolean {
  return of.type === 'marbles' || (of.type === 'custom' && of.whole);
}

function formats(currency: string) {
  let found = currencyFormats.get(currency);
  if (!found) {
    // The device's own locale, so a euro reads "12,50 €" in Berlin and "€12.50"
    // in Dublin; `narrowSymbol` so dollars read "$" rather than "US$" outside
    // the US.
    const base = { style: 'currency', currency, currencyDisplay: 'narrowSymbol' } as const;
    found = {
      full: new Intl.NumberFormat(undefined, base),
      short: new Intl.NumberFormat(undefined, { ...base, minimumFractionDigits: 0, maximumFractionDigits: 0 }),
    };
    currencyFormats.set(currency, found);
  }
  return found;
}

function counted(hundredths: number, of: Unit, withIcon: boolean): string {
  const n = hundredths / 100;
  const word =
    of.type === 'marbles'
      ? Math.abs(n) === 1 ? 'marble' : 'marbles'
      : of.type === 'custom'
        ? Math.abs(n) === 1 ? of.one : of.many
        : '';
  const icon = withIcon && of.type === 'custom' && of.icon ? `${of.icon} ` : '';
  return `${icon}${plain.format(n)} ${word}`.trim();
}

/**
 * An amount in the family's unit: "$26.00", "26,00 €", "26 marbles", "⭐ 26 stars".
 * Named for what it was before units existed, and kept so, because it is the
 * one formatter the whole app calls.
 */
export function dollars(hundredths: number, of: Unit = unit): string {
  if (of.type === 'currency') return formats(of.currency).full.format(hundredths / 100);
  return counted(hundredths, of, true);
}

/**
 * The short form the valley shows across a room: "$33", "33", "⭐ 33". The
 * word is left off for marbles and custom units, because the jar (or the
 * icon) beside it says what it is.
 */
export function shortAmount(hundredths: number, of: Unit = unit): string {
  if (of.type === 'currency') return formats(of.currency).short.format(hundredths / 100);
  const n = plain.format(hundredths / 100);
  return of.type === 'custom' && of.icon ? `${of.icon} ${n}` : n;
}

/** The unit's plural noun: "money", "marbles", "stars". */
export function unitNoun(of: Unit = unit): string {
  return of.type === 'currency' ? 'money' : of.type === 'marbles' ? 'marbles' : of.many;
}

/** The question over an amount field: "How much?" or "How many marbles?". */
export function howMuch(of: Unit = unit): string {
  return of.type === 'currency' ? 'How much?' : `How many ${unitNoun(of)}?`;
}

/** What an empty amount field shows. */
export function amountPlaceholder(of: Unit = unit): string {
  return isWhole(of) ? '0' : '0.00';
}

/** The keyboard a phone should offer for an amount. */
export function amountInputMode(of: Unit = unit): 'decimal' | 'numeric' {
  return isWhole(of) ? 'numeric' : 'decimal';
}

/**
 * The typed amount, cleaned for the server.
 *
 * A thousands separator is the reason this exists. The server parses with
 * `Decimal(str(value))`, which rejects "1,500" outright -- so a parent typing
 * a goal target the way people write goal targets got a Save button that went
 * grey and said nothing. Stripping the separator here, and sending the
 * stripped form, is what makes the two agree.
 *
 * A comma is a DECIMAL point when it is the only separator and one or two
 * digits follow it -- "12,50" is twelve and a half wherever the euro is
 * written that way -- and a thousands separator otherwise. A symbol or a unit
 * word typed around the number ("€12", "5 marbles") is dropped.
 */
export function normaliseAmount(typed: string): string {
  const bare = typed.trim().replace(/^[^\d.,-]+/, '').replace(/[^\d.,]+$/, '').trim();
  if (/^\d+,\d{1,2}$/.test(bare)) return bare.replace(',', '.');
  return bare.replace(/,/g, '');
}

/**
 * True when a typed amount is something the server will accept. Whole
 * numbers only when the family counts in whole units.
 *
 * Deliberately NOT a parser beyond that: it decides whether the button is
 * enabled, and the server decides what the number is.
 */
export function looksLikeAmount(typed: string, of: Unit = unit): boolean {
  const clean = normaliseAmount(typed);
  return isWhole(of) ? /^\d{1,7}$/.test(clean) : /^\d{1,7}(\.\d{1,2})?$/.test(clean);
}

/**
 * The amount as the server wants it: the typed string, with a minus sign in
 * front when the parent chose "spent".
 *
 * Prefixing a character is not arithmetic. The parsing, the rounding and the
 * split still all happen in domain.py.
 */
export function signed(typed: string, direction: 'in' | 'out'): string {
  const clean = normaliseAmount(typed);
  return direction === 'out' ? `-${clean}` : clean;
}

/**
 * Hundredths back into the string form the server parses: "12.50", or "12"
 * for a whole-unit family's whole amount.
 *
 * `toFixed(2)` rather than dividing and stringifying, because `String(n / 100)`
 * gives "2.5" for 250 and "0.1" for 10. One shape on the wire is worth more
 * than the characters it saves.
 *
 * This is formatting. No split, no rounding of a share, nothing that could
 * disagree with the ledger -- all of that is in domain.py.
 */
export function asTyped(hundredths: number, of: Unit = unit): string {
  if (isWhole(of) && hundredths % 100 === 0) return String(hundredths / 100);
  return (hundredths / 100).toFixed(2);
}

/**
 * An amount split for display at a large size: the number, and the unit word
 * to set smaller beside it. Money keeps its symbol in the number and has no
 * word; "15 marbles" is "15" and "marbles".
 */
export function bigAmount(hundredths: number, of: Unit = unit): { number: string; word: string } {
  if (of.type === 'currency') return { number: dollars(hundredths, of), word: '' };
  const n = hundredths / 100;
  const word = of.type === 'marbles' ? (Math.abs(n) === 1 ? 'marble' : 'marbles') : Math.abs(n) === 1 ? of.one : of.many;
  return { number: shortAmount(hundredths, of), word };
}

/** The label for adding: "Money in", "Marbles in", "Stars in". */
export function inLabel(of: Unit = unit): string {
  const noun = unitNoun(of);
  return `${noun.charAt(0).toUpperCase()}${noun.slice(1)} in`;
}
